/**
 * Mock data for the AI Wealth Advisor sub-page
 * Based on Alexis Siegel's dashboard situation:
 * - 34 years old, Strasbourg, no children
 * - Monthly income: 6500€, expenses ~5000€
 * - Net worth: -976€ (negative)
 * - 100% in current account (zero diversification)
 * - 0 objectives set
 *
 * The mock is intentionally a "patrimoine en mauvaise santé" so the demo
 * showcases the full power of the advisor (low score, many actionable
 * suggestions, dramatic before/after simulations).
 *
 * ======================================================================
 * LAP-CP05 — TODO INTÉGRATION DONNÉES UTILISATEUR RÉELLES
 * ======================================================================
 *
 * STATUS ACTUEL : tout est mocké. Le conseiller N'utilise PAS encore :
 *   - les objectifs financiers de l'utilisateur (Mes Objectifs)
 *   - ses centres d'intérêts financiers (Profil Investisseur)
 *   - son niveau de connaissance financière (Questionnaire de connaissance)
 *   - son profil de risque (Déterminer votre profil risque)
 *   - son patrimoine réel (Mon Patrimoine card)
 *   - son budget réel (Mon Budget card)
 *
 * PLAN D'INTÉGRATION RÉELLE :
 *
 *  1) Côté Redux store, lire dans le composant page ConseillerPatrimonialPage :
 *       const siteUser = useSelector((s) => s.user)
 *       const { firstName, lastName, age, city, has_subscription_type } =
 *         siteUser.user
 *
 *  2) Côté API, ajouter ces appels au mount de la page :
 *       GET /api/general/mon-bilan-patrimonial-reports/        → patrimoine, budget, portrait, objectifs (déjà appelé par MonPatrimoine/MainBoard.js)
 *       GET /api/general/investor-profile/                     → risque + connaissance + intérêts (à exposer côté backend)
 *       GET /api/general/mes-objectifs/                        → objectifs définis
 *
 *  3) Backend : nouvel endpoint pour le SCORE patrimonial calculé
 *       POST /api/general/patrimoine/compute-score/
 *         input  : { user_id }
 *         output : { score, grade, dimensions, percentile, trend, diagnostic }
 *       Le scoring engine actuel (utils/scoringEngine.js) montre les règles
 *       métier — peut servir de référence pour l'implémentation backend.
 *
 *  4) Backend : nouvel endpoint pour la GÉNÉRATION du plan d'action IA
 *       POST /api/general/patrimoine/generate-advice/
 *         input  : { user_id, locale }
 *         output : { actionsPrioritaires, projetsSugeres, simulationProjection }
 *         coût   : 1 crédit (cf. spec pricing 0,20€/crédit, marge 80%)
 *       Le prompt LLM doit injecter :
 *         - profil utilisateur (âge, ville, situation familiale)
 *         - profil de risque (prudent / équilibré / dynamique)
 *         - niveau de connaissance (débutant / intermédiaire / expert)
 *         - centres d'intérêts (immo, marchés, éthique…)
 *         - objectifs définis (retraite, RP, transmission…)
 *         - patrimoine actuel (montants, répartition)
 *         - locale (FR/EN/...) ⚠ cf. bug langue allemande
 *
 *  5) Côté front, remplacer les `import { userProfile, patrimoineScore,
 *     diagnostic, actionsPrioritaires, projetsSugeres, projection20ans }
 *     from '../data/mockPatrimoineData'` par les données venant des APIs
 *     ci-dessus. Tant que les endpoints (3) et (4) n'existent pas, on garde
 *     les mocks pour que l'UI soit testable.
 *
 *  6) Pour la PERSONNALISATION minimale immédiate (sans backend) : remplacer
 *     `userProfile.firstName = 'Alexis'` par la lecture du store utilisateur
 *     dans la page (siteUser.user.first_name). Le reste reste mocké mais au
 *     moins le prénom affiché est le vrai.
 *
 * ======================================================================
 */

export const userProfile = {
  firstName: 'Alexis',
  lastName: 'Siegel',
  age: 34,
  city: 'Strasbourg',
  children: 0,
  monthlyIncome: 6500,
  monthlyExpenses: 5000,
  netWorth: -976,
  liquiditeRatio: 1.0, // 100% en compte courant
  hasInvestmentProfile: false,
  hasObjectives: false,
}

/**
 * Score patrimonial global et décomposition par dimension.
 * Le scoring engine est documenté dans utils/scoringEngine.js
 */
export const patrimoineScore = {
  global: 38, // /100
  grade: 'D',
  trend: '-3', // évolution vs mois précédent
  percentile: 22, // dans la médiane des utilisateurs même tranche d'âge

  dimensions: [
    {
      key: 'epargne',
      label: 'Épargne de précaution',
      score: 15,
      weight: 0.2,
      status: 'critical',
      detail: 'Vous n\'avez pas d\'épargne de sécurité. La règle des 3 à 6 mois de dépenses (15 000€ à 30 000€) est non couverte.',
    },
    {
      key: 'diversification',
      label: 'Diversification',
      score: 5,
      weight: 0.2,
      status: 'critical',
      detail: '100% de votre patrimoine est sur un compte courant. Aucune exposition aux marchés, à l\'immobilier ou aux livrets réglementés.',
    },
    {
      key: 'endettement',
      label: 'Endettement',
      score: 60,
      weight: 0.15,
      status: 'warning',
      detail: 'Cashflow mensuel positif mais patrimoine net négatif (-976€). À surveiller.',
    },
    {
      key: 'prevoyance',
      label: 'Prévoyance & retraite',
      score: 30,
      weight: 0.2,
      status: 'warning',
      detail: 'Aucun PER ou contrat d\'assurance-vie identifié. À 34 ans, chaque année compte pour la capitalisation.',
    },
    {
      key: 'fiscalite',
      label: 'Optimisation fiscale',
      score: 25,
      weight: 0.15,
      status: 'warning',
      detail: 'À TMI estimée 30% (revenus typiques), un PER ou autre dispositif de défiscalisation permettrait d\'économiser plusieurs milliers d\'euros par an.',
    },
    {
      key: 'objectifs',
      label: 'Pilotage par objectifs',
      score: 0,
      weight: 0.1,
      status: 'critical',
      detail: 'Aucun objectif patrimonial défini. Sans cible, impossible de mesurer la trajectoire.',
    },
  ],
}

export const diagnostic = {
  forces: [
    {
      id: 'f1',
      title: 'Cashflow mensuel positif',
      description: 'Vous dégagez environ 1 500€ d\'épargne potentielle chaque mois (23% de taux d\'épargne théorique).',
      icon: 'TrendingUp',
    },
    {
      id: 'f2',
      title: 'Profil jeune actif',
      description: 'À 34 ans, l\'horizon long permet d\'absorber la volatilité des marchés et de capitaliser sur les intérêts composés.',
      icon: 'AccessTime',
    },
    {
      id: 'f3',
      title: 'Revenus stables et élevés',
      description: 'Revenu mensuel de 6 500€, supérieur de 78% à la médiane française. Une excellente base.',
      icon: 'AccountBalance',
    },
  ],

  faiblesses: [
    {
      id: 'w1',
      title: 'Patrimoine net négatif',
      severity: 'high',
      description: 'Votre patrimoine est de -976€. Vous devez d\'abord reconstituer une assise positive avant d\'investir.',
      icon: 'TrendingDown',
    },
    {
      id: 'w2',
      title: 'Concentration à 100% sur le compte courant',
      severity: 'high',
      description: 'Aucune rémunération (compte courant = 0%), aucune protection contre l\'inflation (-2% de pouvoir d\'achat / an).',
      icon: 'PieChart',
    },
    {
      id: 'w3',
      title: 'Aucun objectif patrimonial',
      severity: 'medium',
      description: 'Sans projet défini (retraite, immobilier, transmission), le patrimoine se construit sans direction.',
      icon: 'Flag',
    },
  ],

  risques: [
    {
      id: 'r1',
      title: 'Risque de coup dur non couvert',
      probability: 'high',
      impact: 'critical',
      description: 'Aucune épargne disponible : une dépense imprévue de 2 000€ vous mettrait en découvert structurel.',
    },
    {
      id: 'r2',
      title: 'Érosion par l\'inflation',
      probability: 'certain',
      impact: 'medium',
      description: 'À 2% d\'inflation, votre pouvoir d\'achat baisse de ~130€/an si rien n\'est placé.',
      annualLoss: 130,
    },
    {
      id: 'r3',
      title: 'Manque à gagner retraite',
      probability: 'certain',
      impact: 'high',
      description: 'Estimation : pension publique à 55% du dernier salaire. Sans complément, baisse de niveau de vie significative.',
    },
  ],
}

/**
 * Actions priorisées par le score Impact x Faisabilité.
 * Chaque action peut être lancée en 1 clic et créer un Projet
 * (cf. mockProjets ci-dessous).
 */
export const actionsPrioritaires = [
  {
    id: 'a1',
    rank: 1,
    title: 'Ouvrir un Livret A et y verser 200€/mois',
    category: 'Épargne de précaution',
    impact: 9,
    facilite: 10,
    horizon: 'immediate',
    horizonLabel: '7 jours',
    gainAnnuel: 78,
    gainDescription: 'Intérêts à 3% nets d\'impôts, disponibilité immédiate',
    scoreImpact: '+12 points', // impact sur le score global
    explanation: 'Le Livret A est la brique de base de toute épargne de précaution. Plafond 22 950€, 3% net garanti, retrait sans pénalité.',
    cta: 'Créer le projet épargne',
    projetId: 'p1',
    bestFor: ['Épargne de précaution', 'Premier réflexe'],
    tags: ['Sans risque', 'Liquide', '3% net'],
  },
  {
    id: 'a2',
    rank: 2,
    title: 'Activer un PER pour défiscaliser ~2 400€/an',
    category: 'Optimisation fiscale',
    impact: 8,
    facilite: 7,
    horizon: 'short',
    horizonLabel: '30 jours',
    gainAnnuel: 2400,
    gainDescription: 'Économie d\'impôt sur 8 000€ versés à TMI 30%',
    scoreImpact: '+15 points',
    explanation: 'Plan d\'Épargne Retraite : les versements sont déductibles du revenu imposable. À votre TMI de 30%, 8 000€ versés vous reviennent à 5 600€ net.',
    cta: 'Simuler mon PER',
    projetId: 'p2',
    bestFor: ['TMI > 30%', 'Horizon retraite long'],
    tags: ['Défiscalisation', 'Blocage retraite', 'Effet de levier'],
  },
  {
    id: 'a3',
    rank: 3,
    title: 'Ouvrir une assurance-vie multi-supports',
    category: 'Diversification',
    impact: 8,
    facilite: 8,
    horizon: 'short',
    horizonLabel: '30 jours',
    gainAnnuel: 480,
    gainDescription: 'Rendement cible 4% net après 8 ans (fiscalité allégée)',
    scoreImpact: '+10 points',
    explanation: 'Enveloppe la plus souple du marché français : combine fonds euros (sécurité) et unités de compte (croissance). Fiscalité avantageuse après 8 ans.',
    cta: 'Comparer les contrats',
    projetId: 'p3',
    bestFor: ['Capitaliser long terme', 'Préparer la transmission'],
    tags: ['Fiscalité après 8 ans', 'Multi-supports', 'Souple'],
  },
  {
    id: 'a4',
    rank: 4,
    title: 'Mettre en place un virement automatique d\'épargne',
    category: 'Comportement',
    impact: 7,
    facilite: 10,
    horizon: 'immediate',
    horizonLabel: '24 heures',
    gainAnnuel: 18000,
    gainDescription: 'Sur 10 ans, 1 500€/mois investis à 4% = 220 000€',
    scoreImpact: '+8 points',
    explanation: 'Le "pay yourself first" : virer automatiquement 1 500€ vers votre épargne le jour de réception du salaire. La meilleure stratégie comportementale.',
    cta: 'Configurer le virement',
    projetId: 'p4',
    bestFor: ['Régulariser l\'épargne', 'Comportement long terme'],
    tags: ['Automatisation', 'Zéro friction', 'Habitude'],
  },
  {
    id: 'a5',
    rank: 5,
    title: 'Étudier l\'achat de la résidence principale',
    category: 'Patrimoine immobilier',
    impact: 9,
    facilite: 5,
    horizon: 'medium',
    horizonLabel: '6-18 mois',
    gainAnnuel: 9600,
    gainDescription: 'Capital remboursé / an vs loyer perdu (estimation locale)',
    scoreImpact: '+18 points',
    explanation: 'Capacité d\'emprunt estimée à 280 000€ sur 25 ans (33% endettement). Le marché immobilier local offre un rendement locatif équivalent moyen de ~4%.',
    cta: 'Lancer le projet immo',
    projetId: 'p5',
    bestFor: ['Constituer un actif tangible', 'Effet de levier crédit'],
    tags: ['Effet de levier', 'Long terme', 'Résidence principale'],
  },
  {
    id: 'a6',
    rank: 6,
    title: 'Ouvrir un PEA et investir 500€/mois en ETF Monde',
    category: 'Marchés financiers',
    impact: 8,
    facilite: 7,
    horizon: 'short',
    horizonLabel: '30 jours',
    gainAnnuel: 360,
    gainDescription: 'Rendement historique MSCI World ~7% / an sur 20 ans',
    scoreImpact: '+12 points',
    explanation: 'PEA : exonération totale d\'impôt après 5 ans (hors prélèvements sociaux). ETF monde = diversification maximale, frais ~0.2%/an.',
    cta: 'Ouvrir mon PEA',
    projetId: 'p6',
    bestFor: ['Investissement long terme', 'Croissance'],
    tags: ['Exonération après 5 ans', 'Diversifié monde', 'Frais bas'],
  },
  {
    id: 'a7',
    rank: 7,
    title: 'Souscrire une prévoyance individuelle',
    category: 'Prévoyance',
    impact: 6,
    facilite: 9,
    horizon: 'short',
    horizonLabel: '30 jours',
    gainAnnuel: 0,
    gainDescription: 'Aucun gain direct mais couverture risques majeurs',
    scoreImpact: '+5 points',
    explanation: 'En cas d\'arrêt de travail long, la sécurité sociale ne couvre que ~50% du salaire net. Une prévoyance comble ce gap.',
    cta: 'Demander des devis',
    projetId: 'p7',
    bestFor: ['Couverture arrêt de travail', 'Protection famille'],
    tags: ['Sécurité', 'Couverture revenus'],
  },
]

/**
 * Projets prêts à être créés en 1 clic.
 * Chaque projet pré-rempli ouvre le wizard de création
 * (qui réutilise les flows existants de MesObjectifs / Investments).
 */
export const projetsSugeres = [
  {
    id: 'p1',
    title: 'Épargne de précaution 6 mois',
    icon: 'Savings',
    targetAmount: 30000,
    monthlyContribution: 500,
    duration: '60 mois',
    expectedYield: '3% net',
    vehicle: 'Livret A + LDDS',
    pillar: 'Sécurité',
    color: '#22c55e',
    description: 'Atteindre 30 000€ disponibles immédiatement en cas d\'imprévu majeur.',
    autofilled: {
      objectiveType: 'necessaire_fixe',
      duration_months: 60,
      monthly_amount: 500,
      target_amount: 30000,
      vehicles: ['livret_a', 'ldds'],
    },
  },
  {
    id: 'p2',
    title: 'PER - Préparation retraite',
    icon: 'Elderly',
    targetAmount: 200000,
    monthlyContribution: 670,
    duration: '25 ans',
    expectedYield: '5% brut + 30% défisc',
    vehicle: 'PER individuel',
    pillar: 'Long terme',
    color: '#8b5cf6',
    description: 'Capital retraite complémentaire avec défiscalisation immédiate.',
    autofilled: {
      objectiveType: 'secondaire_fixe',
      duration_months: 300,
      monthly_amount: 670,
      target_amount: 200000,
      vehicles: ['per_individuel'],
      tax_optimized: true,
    },
  },
  {
    id: 'p3',
    title: 'Assurance-vie multi-supports',
    icon: 'Shield',
    targetAmount: 50000,
    monthlyContribution: 300,
    duration: '8 ans',
    expectedYield: '4% net visé',
    vehicle: 'Assurance-vie',
    pillar: 'Croissance',
    color: '#0ea5e9',
    description: 'Enveloppe souple pour faire travailler un capital sur le moyen-long terme.',
    autofilled: {
      objectiveType: 'secondaire_variable',
      duration_months: 96,
      monthly_amount: 300,
      vehicles: ['assurance_vie'],
    },
  },
  {
    id: 'p5',
    title: 'Résidence principale',
    icon: 'Home',
    targetAmount: 280000,
    monthlyContribution: 1400, // mensualité estimée
    duration: '25 ans',
    expectedYield: 'Capital constitué + résidence',
    vehicle: 'Prêt immobilier',
    pillar: 'Immobilier',
    color: '#f59e0b',
    description: 'Acquisition de votre résidence principale, premier brique du patrimoine.',
    autofilled: {
      objectiveType: 'necessaire_fixe',
      duration_months: 300,
      monthly_amount: 1400,
      target_amount: 280000,
      vehicles: ['pret_immobilier'],
    },
  },
  {
    id: 'p6',
    title: 'PEA ETF Monde',
    icon: 'ShowChart',
    targetAmount: 80000,
    monthlyContribution: 500,
    duration: '15 ans',
    expectedYield: '7% historique',
    vehicle: 'PEA + ETF MSCI World',
    pillar: 'Croissance',
    color: '#ec4899',
    description: 'Capitalisation long terme sur les marchés mondiaux, fiscalité optimisée.',
    autofilled: {
      objectiveType: 'secondaire_variable',
      duration_months: 180,
      monthly_amount: 500,
      vehicles: ['pea', 'etf_world'],
    },
  },
]

/**
 * Comparatif de la projection à 20 ans
 * - "actuel" : trajectoire telle qu'elle est aujourd'hui
 * - "avecActions" : si les 3 premières actions sont mises en place
 */
export const projection20ans = {
  actuel: generateLinearProjection(-976, -215, 20),
  avecActions: generateGrowthProjection(-976, 1500, 0.045, 20),
}

function generateLinearProjection(start, delta, years) {
  const arr = []
  for (let y = 0; y <= years; y++) {
    arr.push({ year: 2026 + y, value: Math.round(start + delta * y) })
  }
  return arr
}

function generateGrowthProjection(start, monthlyContrib, annualRate, years) {
  const arr = []
  let val = start
  for (let y = 0; y <= years; y++) {
    arr.push({ year: 2026 + y, value: Math.round(val) })
    val = val * (1 + annualRate) + monthlyContrib * 12
  }
  return arr
}

/**
 * LAP-748 / LAP-i18n — Builder paramétrique pour le message initial du chat conseiller.
 * AVANT : `initialChatMessages` était un export statique avec "Bonjour Alexis"
 * hardcodé, qui s'affichait pour TOUS les utilisateurs (bug privacy critique).
 * MAINTENANT : on construit le message dynamiquement à partir du vrai prénom
 * de l'utilisateur connecté ET d'une fonction de traduction `t` injectée par
 * le composant React. Quand `t` est omis (rétrocompatibilité), on tombe sur
 * les textes FR codés par défaut.
 */
export function buildInitialChatMessages(firstName, t) {
  const name = (firstName || '').trim()
  let greeting
  let body
  if (typeof t === 'function') {
    greeting = name
      ? t('conseillerPatrimonial.initialChat.greetingWithName', { name })
      : t('conseillerPatrimonial.initialChat.greetingNoName')
    body = t('conseillerPatrimonial.initialChat.body')
  } else {
    greeting = name ? `Bonjour ${name}.` : 'Bonjour.'
    body =
      "Voici un aperçu type de l'analyse que votre Life Agent peut produire à partir de vos données. Les chiffres affichés sont des exemples : votre score réel, vos actions prioritaires et vos projets seront calculés dès que votre profil sera complet (objectifs, profil de risque, connaissance financière, centres d'intérêt). En attendant, explorez les actions et projets ci-dessous pour comprendre la méthode du Life Agent."
  }
  return [
    {
      role: 'agent',
      content: `${greeting} ${body}`,
      source: 'Life Agent',
      timestamp: new Date().toISOString(),
    },
  ]
}

export const initialChatMessages = buildInitialChatMessages('')

export const suggestedPrompts = [
  'Comment augmenter mon score patrimonial le plus vite ?',
  'Combien je peux investir sans risque chaque mois ?',
  'Est-ce le bon moment pour acheter ma résidence principale ?',
  'Quel est l\'impact fiscal d\'un PER pour moi ?',
  'Compare PER et assurance-vie pour mon profil',
]
